import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Loader2, Plus, Users, Calendar, Building2, Trash2, Save, X, UserPlus, ShieldCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATE_BADGE = {
  active: { label: 'Ativa', cls: 'bg-emerald-900/50 text-emerald-400 border-emerald-700' },
  expired: { label: 'Expirada', cls: 'bg-red-900/50 text-red-400 border-red-700' },
  scheduled: { label: 'Agendada', cls: 'bg-sky-900/50 text-sky-400 border-sky-700' },
  suspended: { label: 'Suspensa', cls: 'bg-slate-700/50 text-slate-300 border-slate-600' },
  quota_reached: { label: 'Sem quota', cls: 'bg-amber-900/50 text-amber-400 border-amber-700' },
};

const EMPTY_FORM = {
  name: '', contact_name: '', contact_email: '', contact_phone: '',
  plan_label: '', valid_from: '', valid_until: '',
  max_events: '', max_participants_per_event: '', notes: '',
};

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function AccountsManager() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [users, setUsers] = useState([]);
  const [assignUserId, setAssignUserId] = useState('');

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/accounts`, { headers: authHeaders() });
      setAccounts(res.data);
    } catch {
      toast.error('Erro ao carregar contas');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: authHeaders() });
      setUsers(res.data.filter((u) => u.role !== 'admin'));
    } catch { /* noop */ }
  }, []);

  useEffect(() => { fetchAccounts(); fetchUsers(); }, [fetchAccounts, fetchUsers]);

  const openCreate = () => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); };
  const openEdit = (acc) => {
    setForm({
      name: acc.name || '', contact_name: acc.contact_name || '', contact_email: acc.contact_email || '',
      contact_phone: acc.contact_phone || '', plan_label: acc.plan_label || '',
      valid_from: acc.valid_from || '', valid_until: acc.valid_until || '',
      max_events: acc.max_events ?? '', max_participants_per_event: acc.max_participants_per_event ?? '',
      notes: acc.notes || '',
    });
    setEditingId(acc.id);
    setShowForm(true);
  };

  const submitForm = async () => {
    if (!form.name.trim()) { toast.error('O nome da organização é obrigatório'); return; }
    setSaving(true);
    const payload = {
      ...form,
      max_events: form.max_events === '' ? null : Number(form.max_events),
      max_participants_per_event: form.max_participants_per_event === '' ? null : Number(form.max_participants_per_event),
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/accounts/${editingId}`, payload, { headers: authHeaders() });
        toast.success('Conta atualizada');
      } else {
        await axios.post(`${API}/admin/accounts`, payload, { headers: authHeaders() });
        toast.success('Conta criada');
      }
      setShowForm(false);
      fetchAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar a conta');
    } finally {
      setSaving(false);
    }
  };

  const toggleSuspend = async (acc) => {
    const next = acc.status === 'active' ? 'suspended' : 'active';
    try {
      await axios.put(`${API}/admin/accounts/${acc.id}`, { status: next }, { headers: authHeaders() });
      toast.success(next === 'suspended' ? 'Conta suspensa (bloqueio imediato)' : 'Conta reativada');
      fetchAccounts();
      if (detail?.id === acc.id) openDetail(acc.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const removeAccount = async (acc) => {
    if (!window.confirm(`Eliminar a conta "${acc.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/accounts/${acc.id}`, { headers: authHeaders() });
      toast.success('Conta eliminada');
      if (detail?.id === acc.id) setDetail(null);
      fetchAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao eliminar');
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/accounts/${id}`, { headers: authHeaders() });
      setDetail(res.data);
      setAssignUserId('');
    } catch {
      toast.error('Erro ao carregar detalhe');
    }
  };

  const assignUser = async () => {
    if (!assignUserId) return;
    try {
      await axios.post(`${API}/admin/users/${assignUserId}/account`, { account_id: detail.id }, { headers: authHeaders() });
      toast.success('Utilizador associado');
      openDetail(detail.id);
      fetchAccounts();
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao associar');
    }
  };

  const unlinkUser = async (uid) => {
    try {
      await axios.post(`${API}/admin/users/${uid}/account`, { account_id: null }, { headers: authHeaders() });
      toast.success('Associação removida');
      openDetail(detail.id);
      fetchAccounts();
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;
  }

  const inputCls = 'bg-slate-800 border-slate-700 text-white';
  const unassignedUsers = users.filter((u) => !u.account_id);

  return (
    <div className="space-y-6" data-testid="accounts-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">{accounts.length} conta(s) gerida(s)</p>
        <Button onClick={openCreate} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="create-account-btn">
          <Plus className="w-4 h-4 mr-2" /> Nova Conta
        </Button>
      </div>

      {showForm && (
        <Card className="bg-slate-900 border-cyan-900/60" data-testid="account-form">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white text-lg">{editingId ? 'Editar Conta' : 'Nova Conta'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-slate-400"><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-400">Organização *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="account-name-input" /></div>
              <div><label className="text-xs text-slate-400">Plano (etiqueta)</label>
                <Input value={form.plan_label} onChange={(e) => setForm({ ...form, plan_label: e.target.value })} placeholder="Evento Pontual" className={inputCls} data-testid="account-plan-input" /></div>
              <div><label className="text-xs text-slate-400">Contacto (nome)</label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} /></div>
              <div><label className="text-xs text-slate-400">Contacto (email)</label>
                <Input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className={inputCls} /></div>
              <div><label className="text-xs text-slate-400">Válida de</label>
                <Input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className={inputCls} data-testid="account-valid-from-input" /></div>
              <div><label className="text-xs text-slate-400">Válida até</label>
                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className={inputCls} data-testid="account-valid-until-input" /></div>
              <div><label className="text-xs text-slate-400">Nº máx. de eventos (vazio = ilimitado)</label>
                <Input type="number" min="0" value={form.max_events} onChange={(e) => setForm({ ...form, max_events: e.target.value })} className={inputCls} data-testid="account-max-events-input" /></div>
              <div><label className="text-xs text-slate-400">Máx. participantes / evento (vazio = ilimitado)</label>
                <Input type="number" min="1" value={form.max_participants_per_event} onChange={(e) => setForm({ ...form, max_participants_per_event: e.target.value })} className={inputCls} data-testid="account-max-participants-input" /></div>
            </div>
            <div><label className="text-xs text-slate-400">Notas internas</label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></div>
            <div className="flex gap-2">
              <Button onClick={submitForm} disabled={saving} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="save-account-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Guardar
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-slate-700 text-slate-300">Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Accounts list */}
        <Card className="bg-slate-900 border-slate-800" data-testid="accounts-list-card">
          <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-cyan-400" /> Contas</CardTitle></CardHeader>
          <CardContent>
            {accounts.length === 0 ? (
              <p className="text-slate-400 text-center py-8" data-testid="no-accounts">Nenhuma conta criada.</p>
            ) : (
              <div className="space-y-3">
                {accounts.map((acc) => {
                  const badge = STATE_BADGE[acc.computed_state] || STATE_BADGE.active;
                  return (
                    <div key={acc.id} onClick={() => openDetail(acc.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${detail?.id === acc.id ? 'border-cyan-500 bg-cyan-950/30' : 'border-slate-700 hover:border-slate-600 hover:bg-slate-800'}`}
                      data-testid={`account-item-${acc.id}`}>
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-['Manrope'] font-bold text-white">{acc.name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${badge.cls}`} data-testid={`account-state-${acc.id}`}>{badge.label}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                        {acc.plan_label && <span>{acc.plan_label}</span>}
                        <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {acc.user_count}</span>
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {acc.valid_from || '—'} → {acc.valid_until || '∞'}</span>
                        <span>Eventos: {acc.events_used}{acc.max_events != null ? `/${acc.max_events}` : ' (ilim.)'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail panel */}
        <Card className="bg-slate-900 border-slate-800" data-testid="account-detail-card">
          <CardHeader><CardTitle className="text-white text-lg">Detalhe da Conta</CardTitle></CardHeader>
          <CardContent>
            {!detail ? (
              <p className="text-slate-400 text-center py-8" data-testid="select-account-message">Selecione uma conta para ver o detalhe.</p>
            ) : (
              <div className="space-y-4" data-testid="account-detail">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold text-lg">{detail.name}</p>
                    <p className="text-xs text-slate-500">{detail.plan_label || 'Sem plano'} · criada por {detail.created_by}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${(STATE_BADGE[detail.computed_state] || STATE_BADGE.active).cls}`}>{(STATE_BADGE[detail.computed_state] || STATE_BADGE.active).label}</span>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-slate-800 rounded p-3"><p className="text-slate-500">Validade</p><p className="text-white">{detail.valid_from || '—'} → {detail.valid_until || '∞'}</p></div>
                  <div className="bg-slate-800 rounded p-3"><p className="text-slate-500">Eventos</p><p className="text-white">{detail.events_used}{detail.max_events != null ? ` / ${detail.max_events}` : ' (ilimitado)'}</p></div>
                  <div className="bg-slate-800 rounded p-3"><p className="text-slate-500">Participantes / evento</p><p className="text-white">{detail.max_participants_per_event ?? 'ilimitado'}</p></div>
                  <div className="bg-slate-800 rounded p-3"><p className="text-slate-500">Contacto</p><p className="text-white truncate">{detail.contact_name || '—'}{detail.contact_email ? ` · ${detail.contact_email}` : ''}</p></div>
                </div>
                {detail.notes && <p className="text-xs text-slate-400 italic border-l-2 border-slate-700 pl-2">{detail.notes}</p>}

                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => openEdit(detail)} className="bg-slate-700 hover:bg-slate-600 text-white" data-testid="edit-account-btn"><Save className="w-4 h-4 mr-1" /> Editar</Button>
                  <Button size="sm" onClick={() => toggleSuspend(detail)} className={detail.status === 'active' ? 'bg-amber-700 hover:bg-amber-600 text-white' : 'bg-emerald-700 hover:bg-emerald-600 text-white'} data-testid="toggle-suspend-btn">
                    {detail.status === 'active' ? <><ShieldAlert className="w-4 h-4 mr-1" /> Suspender</> : <><ShieldCheck className="w-4 h-4 mr-1" /> Reativar</>}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => removeAccount(detail)} className="bg-red-800 hover:bg-red-700" data-testid="delete-account-btn"><Trash2 className="w-4 h-4 mr-1" /> Eliminar</Button>
                </div>

                {/* Users */}
                <div className="border-t border-slate-800 pt-3">
                  <p className="text-sm text-white font-semibold mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> Utilizadores ({(detail.users || []).length})</p>
                  <div className="space-y-1.5 mb-3">
                    {(detail.users || []).length === 0 && <p className="text-xs text-slate-500">Nenhum utilizador associado.</p>}
                    {(detail.users || []).map((u) => (
                      <div key={u.id} className="flex items-center justify-between bg-slate-800 rounded px-3 py-1.5" data-testid={`account-user-${u.id}`}>
                        <span className="text-xs text-slate-300">{u.name} <span className="text-slate-500">· {u.email}</span></span>
                        <button onClick={() => unlinkUser(u.id)} className="text-red-400 hover:text-red-300 text-xs" data-testid={`unlink-user-${u.id}`}>Remover</button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <select value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)}
                      className="flex-1 bg-slate-800 border border-slate-700 rounded text-white text-xs px-2 py-2" data-testid="assign-user-select">
                      <option value="">Associar utilizador…</option>
                      {unassignedUsers.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email}</option>)}
                    </select>
                    <Button size="sm" onClick={assignUser} disabled={!assignUserId} className="bg-cyan-600 hover:bg-cyan-500 text-white" data-testid="assign-user-btn"><UserPlus className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
